// src/firebase.js

import { getApp, getApps, initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import {
  getFirestore,
  // connectFirestoreEmulator      // ← import emulator connector
} from "firebase/firestore";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getMessaging, getToken, isSupported as isMessagingSupported, onMessage } from "firebase/messaging";
import { getFunctions } from "firebase/functions";
import { configureAuthPersistence } from "./services/authPersistence";
import { initializeFirebaseMessaging } from "./utils/firebaseMessaging";

// App Check imports for reCAPTCHA Enterprise
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider
} from "firebase/app-check";

const APP_ENV = String(process.env.REACT_APP_ENV || "").toLowerCase();
const IS_PROD_LIKE_ENV =
  APP_ENV === "staging" ||
  APP_ENV === "preprod" ||
  APP_ENV === "prod" ||
  APP_ENV === "production";

const isIOSRuntime = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const isIOSUA = /iPad|iPhone|iPod/i.test(ua);
  const isIPadOS =
    platform === "MacIntel" &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  return isIOSUA || isIPadOS;
};

const isAndroidRuntime = () => {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
};

const resolveAuthDomain = () => {
  const envAuthDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
  const firebaseAppDomain = projectId ? `${projectId}.firebaseapp.com` : envAuthDomain;

  // Keep existing iOS behavior in prod-like envs.
  if (IS_PROD_LIKE_ENV && isIOSRuntime()) {
    return envAuthDomain;
  }

  // Use Firebase-hosted auth domain on Android in prod-like envs.
  if (IS_PROD_LIKE_ENV && isAndroidRuntime()) {
    return firebaseAppDomain;
  }

  return envAuthDomain;
};

const firebaseConfig = {
  apiKey:             process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:         resolveAuthDomain(),
  projectId:          process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:      process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId:  process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:              process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId:      process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// 1) Initialize core Firebase
const app = initializeApp(firebaseConfig);

const studioFirebaseConfig = {
  apiKey: process.env.REACT_APP_STUDIO_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_STUDIO_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_STUDIO_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STUDIO_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_STUDIO_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_STUDIO_FIREBASE_APP_ID,
};

const hasStudioFirebaseConfig = [
  studioFirebaseConfig.apiKey,
  studioFirebaseConfig.authDomain,
  studioFirebaseConfig.projectId,
  studioFirebaseConfig.appId,
].every((value) => Boolean(String(value || "").trim()));

const studioDatabaseId = String(
  process.env.REACT_APP_STUDIO_FIRESTORE_DATABASE_ID ||
  process.env.REACT_APP_STUDIO_DATABASE_ID ||
  "studio"
).trim();

let studioDb = getFirestore(app, studioDatabaseId);
if (hasStudioFirebaseConfig) {
  const studioAppName = "studio";
  const existingStudioApp = getApps().find((candidate) => candidate.name === studioAppName);
  const studioApp = existingStudioApp || initializeApp(studioFirebaseConfig, studioAppName);
  studioDb = getFirestore(studioApp, studioDatabaseId);
} else {
  try {
    const fallbackStudioApp = getApp("studio");
    studioDb = getFirestore(fallbackStudioApp, studioDatabaseId);
  } catch {
    // Use the default app when no dedicated Studio Firebase app is configured.
  }
}

// 2) Wire up App Check with reCAPTCHA Enterprise when enabled.
//    For local development, we skip App Check by default to avoid noisy 403s.
const appCheckSiteKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY;
const appCheckExplicitlyEnabled = String(process.env.REACT_APP_ENABLE_APPCHECK || "").toLowerCase() === "true";
const shouldEnableAppCheck = Boolean(appCheckSiteKey) && appCheckExplicitlyEnabled;

if (shouldEnableAppCheck) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
    console.info("App Check enabled.");
  } catch (err) {
    console.warn("App Check init failed; continuing without App Check in this environment.", err);
  }
} else {
  // App Check is opt-in for this app/environment.
}

// 3) Create Auth. Phone-auth reCAPTCHA is initialized only on phone flows.
const auth = getAuth(app);

// 4) Initialize Analytics
const analytics = getAnalytics(app);

// 5) Initialize Firestore
const db = getFirestore(app);

// ─── Emulator hookup (development only) ────────────────────────────────────────
// if (window.location.hostname === "localhost") {
//   // Ensure these match your emulator’s host & port
//   connectFirestoreEmulator(db, "localhost", 8080);
// }
// ────────────────────────────────────────────────────────────────────────────────

// 6) Initialize other services
const storage   = getStorage(app);
const functions = getFunctions(app);

// 7) Initialize Messaging after Firebase's async support check.
//    `messaging` stays a live binding for existing callers, but anything that
//    runs during startup must await `messagingReady` — reading `messaging` while
//    the support check is still in flight used to silently disable push for the
//    whole session.
let messaging;
const messagingReady = initializeFirebaseMessaging({
  app,
  isSupported: isMessagingSupported,
  getMessaging,
  onMessage,
}).then((messagingInstance) => {
  if (messagingInstance) {
    messaging = messagingInstance;
  }
  return messagingInstance;
});

// Set persistence once when app starts. Private/storage-limited tabs can reject
// durable browser persistence, so fall back deliberately and expose the result.
configureAuthPersistence({
  auth,
  setPersistence,
  localPersistence: browserLocalPersistence,
  sessionPersistence: browserSessionPersistence,
  memoryPersistence: inMemoryPersistence,
}).catch((error) => {
  console.error("[auth] Failed to configure persistence:", error);
});
export {
  auth,
  analytics,
  db,            // now points at emulator on localhost
  storage,
  messaging,
  messagingReady,
  getToken,
  onMessage,
  functions,
  studioDb,
};
